import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';


export interface TranslationsResponse {
  translations: Record<string, string>;
}

@Injectable({
  providedIn: 'root'
})
export class TranslationService {

  private http = inject(HttpClient);
  private translate = inject(TranslateService);
  private apiUrl = environment.baseUrl;

  private loadedTokenHash: string | null = null;

  loadTranslations(force = false): Observable<Record<string, string>> {
    const currentToken = sessionStorage.getItem('accessToken');
    const currentTokenHash = this.hashToken(currentToken);

    if (!force && currentTokenHash && currentTokenHash === this.loadedTokenHash) {
      return of({});
    }

    return this.http
      .get<TranslationsResponse>(`${this.apiUrl}translation/getTranslations`)
      .pipe(
        map((response) => response?.translations ?? {}),
        switchMap((translations) =>
          this.applyTranslations(translations, currentTokenHash)
        ),
        catchError((error) => {
          console.warn('Error retrieving translations: ', error);
          return of({});
        })
      );
  }

  reset(): void {
    this.loadedTokenHash = null;
  }

  private hashToken(token: string | null): string | null {
    if (!token) {
      return null;
    }

    let hash = 5381;
    for (let i = 0; i < token.length; i++) {
      hash = (hash * 33) ^ token.charCodeAt(i);
    }

    return (hash >>> 0).toString(16);
  }

  private applyTranslations(
    translations: Record<string, string>,
    tokenHash: string | null
  ): Observable<Record<string, string>> {
    const lang =
      this.translate.currentLang || this.translate.getDefaultLang() || 'es';

    const sanitizedTranslations = this.sanitizeTranslations(translations);

    const mergeTranslations = () => {
      if (sanitizedTranslations && Object.keys(sanitizedTranslations).length > 0) {
        this.translate.setTranslation(lang, sanitizedTranslations, true);
      }
      this.loadedTokenHash = tokenHash;
      return sanitizedTranslations;
    };

    return this.translate.reloadLang(lang).pipe(
      map(() => mergeTranslations()),
      catchError(() => of(mergeTranslations()))
    );
  }

  /**
   * Remove caracteres invisíveis que costumam vir de CMS/planilhas de tradução
   * (hífen suave, espaços de largura zero, quebras de linha literais, etc.) e
   * que fazem o texto quebrar no meio de uma palavra de forma "invisível" na tela.
   * Também normaliza espaços múltiplos para evitar gaps estranhos no layout.
   *
   * Percorre recursivamente o objeto, pois a API pode retornar as traduções
   * tanto no formato plano ("EMSO.048": "...") quanto aninhado
   * ("EMSO": { "048": "..." }).
   */
  private sanitizeTranslations(
    translations: Record<string, unknown>
  ): Record<string, string> {
    return this.sanitizeValue(translations) as Record<string, string>;
  }

  private sanitizeValue(value: unknown): unknown {
    if (typeof value === 'string') {
      return this.sanitizeText(value);
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.sanitizeValue(item));
    }

    if (value && typeof value === 'object') {
      const sanitizedObject: Record<string, unknown> = {};
      for (const key of Object.keys(value as Record<string, unknown>)) {
        sanitizedObject[key] = this.sanitizeValue((value as Record<string, unknown>)[key]);
      }
      return sanitizedObject;
    }

    return value;
  }

  private sanitizeText(value: string): string {
    if (typeof value !== 'string') {
      return value;
    }

    return value
      // Quebras inseridas no meio de uma palavra pelo CMS/planilha devem ser removidas,
      // nao transformadas em espaco, para evitar textos como "comp letar".
      .replace(/([A-Za-zÀ-ÿ0-9])[\r\n]+(?=[A-Za-zÀ-ÿ0-9])/g, '$1')
      // Espacos nao separaveis e variacoes unicode de espaco impedem a quebra
      // natural entre palavras em telas estreitas.
      .replace(/&nbsp;|&#160;|&#xA0;/gi, ' ')
      .replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g, ' ')
      // Hífen suave (soft hyphen) - insere ponto de quebra invisível no meio da palavra
      .replace(/\u00AD/g, '')
      // Espaço de largura zero / joiners invisíveis
      .replace(/[\u200B\u200C\u200D\u2060\uFEFF]/g, '')
      // Entidades HTML de hífen suave (podem vir de CMS e são decodificadas
      // pelo navegador quando o texto é usado com [innerHTML])
      .replace(/&shy;|&#173;|&#xAD;/gi, '')
      // Quebras de linha literais (\r\n, \n, \r) vindas do backend viram espaço
      .replace(/\r\n|\r|\n/g, ' ')
      // Colapsa espaços múltiplos gerados pela substituição acima
      .replace(/\s{2,}/g, ' ')
      .trim();
  }
}


